import { api, fileBase64 } from '../console/api.ts';
import { carrierCell } from '../console/carriers.ts';
import { empty, field, fileField, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon, lineName, q } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has, supervises } from '../console/session.ts';
import { $, act, textInput, toast } from '../console/ui.ts';
import { selectControl, textareaControl } from '../controls.ts';
import { loadCatalog } from './cotizar.ts';
import { actions as planActions, plansSection, profilesSection, resetPlans } from './planes.ts';

/**
 * «Productos y aseguradoras»: the carriers' plans with their coverages, side by side, and each
 * carrier's profile (#307, everyone who sells or manages); for the direction and administration
 * also what the agency can quote, how Nubi works each operation with each carrier, and the
 * carriers' rules (RuleSets) read from their rate sheets, which the direction publishes or rejects.
 */

type MatrixCell = {
  mode: 'API' | 'FILE' | 'RULES' | 'MANUAL' | 'NONE';
  transport: string | null;
  via: string;
  fallback: 'WORK_ORDER' | null;
};
type Matrix = {
  operations: string[];
  rows: {
    carrier: string;
    carrierName: string;
    productLine: string;
    cells: Record<string, MatrixCell>;
  }[];
};
const OPERATION: Record<string, string> = {
  quote: 'Cotizar',
  issue: 'Emitir',
  endorse: 'Endosar',
  renew: 'Renovar',
  cancel: 'Cancelar',
  installments: 'Recibos',
  remit: 'Remesar',
  statement: 'Estado de cuenta',
  claim: 'Siniestros',
};
const MODE: Record<string, { label: string; cls: string }> = {
  API: { label: 'API', cls: 'ok' },
  FILE: { label: 'Archivo', cls: 'info' },
  RULES: { label: 'Reglas', cls: 'info' },
  MANUAL: { label: 'Manual', cls: 'warn' },
  NONE: { label: 'No disponible', cls: 'CLOSED' },
};
/** A desk the pack works by e-mail is not a work order: Nubi sends and reads it. */
const modeChip = (c: MatrixCell) =>
  c.mode === 'MANUAL' && c.transport === 'email' && c.fallback === null
    ? pill('info', 'Mesa por correo')
    : `${pill(MODE[c.mode]?.cls ?? 'info', MODE[c.mode]?.label ?? c.mode)}${c.fallback === 'WORK_ORDER' ? '<span class="via">→ orden de trabajo</span>' : ''}`;

export async function productos() {
  const manage = supervises();
  const head = pageHead(
    'Productos y aseguradoras',
    manage
      ? 'Los planes de tus aseguradoras con sus coberturas, qué puede cotizar tu agencia y cómo trabaja Nubi cada operación con cada aseguradora: en línea, por archivo, con reglas publicadas, por la mesa de correo o con una orden de trabajo.'
      : 'Los planes de tus aseguradoras con sus coberturas, lado a lado, y con quién hablar en cada una.',
  );
  const [plans, profiles] = await Promise.all([plansSection(), profilesSection()]);
  // The matrix and the carriers' rules are the direction's and the administration's.
  if (!manage) return head + plans + profiles;
  const [lines, matrix] = await Promise.all([
    loadCatalog(),
    api<Matrix>('GET', '/v1/catalog/matrix'),
  ]);
  if (!matrix.rows.length)
    return (
      head +
      plans +
      profiles +
      empty(
        'inbox',
        'Sin aseguradoras todavía',
        'Cuando la agencia tenga un nombramiento, aquí verás sus productos y cómo se integra.',
        '',
        2,
      )
    );
  const cells = matrix.rows.flatMap((r) => matrix.operations.map((o) => r.cells[o]));
  const online = cells.filter((c) => c && c.fallback === null).length;
  const name = (line: string) => lines.find((l) => l.productLine === line)?.name ?? line;
  const products = `<section class="card"><div class="card-head"><h2>Productos que ofrece tu agencia</h2></div><div class="table-wrap" data-component="table"><table class="table">
    <thead><tr><th>Producto</th><th>Familia</th><th>Aseguradoras</th><th class="num">Preguntas</th><th>Documentos para emitir</th></tr></thead><tbody>
    ${lines
      .map(
        (
          l,
        ) => `<tr><td><strong>${esc(l.name)}</strong><div class="muted small">Cuestionario versión ${l.questionnaire.version}</div></td>
        <td>${esc(l.familyName)}</td><td>${esc(l.carriers.map((c) => c.name).join(', '))}</td>
        <td class="num">${l.questionnaire.fields.reduce((n, f) => n + (f.fields?.length ?? 1), 0)}</td>
        <td class="small">${esc(l.questionnaire.documents.map((d) => d.label).join(' · '))}</td></tr>`,
      )
      .join('')}</tbody></table></div></section>`;
  const table = `<section class="card"><div class="card-head"><h2>Cómo se hace cada operación</h2><span class="spacer"></span>
    <span class="muted small">${online} de ${cells.length} en línea o por archivo</span></div>
    <p class="note">${icon('alert')}<span>Lo que una aseguradora no hace en línea se pide con una <strong>orden de trabajo</strong> a su mesa de servicio, con su tiempo acordado.</span></p>
    <div class="table-wrap" data-component="table"><table class="table matrix"><thead><tr><th>Aseguradora</th><th>Producto</th>${matrix.operations
      .map((o) => `<th>${esc(OPERATION[o] ?? o)}</th>`)
      .join('')}</tr></thead><tbody>
    ${matrix.rows
      .map(
        (
          r,
        ) => `<tr><td>${carrierCell(r.carrier, r.carrierName)}</td><td class="nowrap">${esc(name(r.productLine))}</td>
        ${matrix.operations.map((o) => `<td class="mode">${r.cells[o] ? modeChip(r.cells[o]) : '—'}</td>`).join('')}</tr>`,
      )
      .join('')}</tbody></table></div></section>`;
  const rules = await api<RuleSetSummary[]>('GET', '/v1/rule-sets');
  return head + plans + profiles + products + table + rulesSection(rules, matrix);
}

// ------------------------------------------------------------------ reglas de aseguradoras

type Provenance = { fileName: string; sha256: string; sheet: string; range: string; label: string };
type RuleSetSummary = {
  id: string;
  carrier: string;
  carrierName: string;
  productLine: string;
  version: number;
  state: 'DRAFT' | 'PROPOSED' | 'PUBLISHED' | 'RETIRED' | 'REJECTED';
  createdAt: string;
  createdBy: string | null;
  published: { at: string; by: string } | null;
  rejection: string | null;
  source: { fileName: string; sha256: string; mappedBy: 'template' | 'llm' } | null;
  plan: { code: string; name: string };
  counts: { rates: number; adjustments: number; underwriting: number; frequencies: number };
  tests: { passed: number; total: number; accuracy: string };
};
type RuleRow = { id: string; label?: string; provenance: Provenance | null };
type RuleSetFull = RuleSetSummary & {
  tables: {
    rating: {
      kind: string;
      rows: (RuleRow & {
        ageFrom: number;
        ageTo: number;
        sex?: string;
        deductibleMinor?: number;
        premiumMinor?: number;
        sumFromMinor?: number;
        sumToMinor?: number;
        ratePerMille?: string;
      })[];
    };
    adjustments: (RuleRow & { percent: string })[];
    underwriting: (RuleRow & { decision: string; code: string; reason: string })[];
    fees: {
      issuanceFee: { amountMinor: number; provenance: Provenance | null };
      vat: { percent: string; provenance: Provenance | null };
      validityDays: { days: number; provenance: Provenance | null };
      frequencies: {
        frequency: string;
        surchargePercent: string;
        installments: number;
        provenance: Provenance | null;
      }[];
    };
  };
  testRun: {
    results: {
      id: string;
      label: string;
      passed: boolean;
      expected: { decision: string; grossMinor: number | null };
      actual: { decision: string; grossMinor: number | null } | null;
      differenceMinor: number | null;
    }[];
  };
  history: { state: string; at: string; by: string; note: string | null }[];
};
const RULE_STATE: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: 'Borrador', cls: 'DRAFT' },
  PROPOSED: { label: 'Propuesta', cls: 'warn' },
  PUBLISHED: { label: 'Publicada', cls: 'ok' },
  RETIRED: { label: 'Retirada', cls: 'CLOSED' },
  REJECTED: { label: 'Rechazada', cls: 'bad' },
};
const ruleState = (s: string) => pill(RULE_STATE[s]?.cls ?? 'info', RULE_STATE[s]?.label ?? s);
const SEX_NAME: Record<string, string> = { F: 'Mujer', M: 'Hombre' };
const FREQ_NAME: Record<string, string> = {
  ANNUAL: 'Anual',
  SEMIANNUAL: 'Semestral',
  QUARTERLY: 'Trimestral',
  MONTHLY: 'Mensual',
};
/** The proposal open under the list (null: none). */
let ruleDetail: RuleSetFull | null = null;
/** "Hoja «Salud - Tarifa» · C7 · Edad 30 a 39 · Mujer · deducible Q2,500.00" */
const source = (p: Provenance | null) =>
  p
    ? `<span class="prov" title="${esc(`${p.fileName} · sha256 ${p.sha256}`)}">«${esc(p.sheet)}» · <span class="mono">${esc(p.range)}</span> · ${esc(p.label)}</span>`
    : '<span class="muted">escrita a mano</span>';

function ruleDetailView(rs: RuleSetFull) {
  const director = has('agency_director');
  const t = rs.tables;
  const failing = rs.tests.total - rs.tests.passed;
  const ratingRows = t.rating.rows
    .map((r) =>
      r.premiumMinor !== undefined
        ? `<tr><td>${r.ageFrom} a ${r.ageTo}</td><td>${esc(SEX_NAME[r.sex ?? ''] ?? r.sex)}</td><td class="num">${q(r.deductibleMinor ?? 0)}</td><td class="num">${q(r.premiumMinor)}</td><td class="small">${source(r.provenance)}</td></tr>`
        : `<tr><td>${r.ageFrom} a ${r.ageTo}</td><td colspan="2">${q(r.sumFromMinor ?? 0)} a ${q(r.sumToMinor ?? 0)}</td><td class="num">${esc(r.ratePerMille)} ‰</td><td class="small">${source(r.provenance)}</td></tr>`,
    )
    .join('');
  const ratingHead =
    t.rating.kind === 'HEALTH_AGE_SEX_DEDUCTIBLE'
      ? '<th>Edad</th><th>Sexo</th><th class="num">Deducible</th><th class="num">Prima anual</th>'
      : '<th>Edad</th><th colspan="2">Suma asegurada</th><th class="num">Tasa por millar</th>';
  const tests = rs.testRun.results
    .map(
      (
        r,
      ) => `<tr><td><strong>${esc(r.id)}</strong><div class="muted small">${esc(r.label)}</div></td>
      <td>${pill(r.expected.decision)} ${r.expected.grossMinor === null ? '' : q(r.expected.grossMinor)}</td>
      <td>${r.actual ? `${pill(r.actual.decision)} ${r.actual.grossMinor === null ? '' : q(r.actual.grossMinor)}` : '—'}</td>
      <td>${r.passed ? pill('ok', 'Pasa') : pill('bad', r.differenceMinor ? `Falla por ${q(r.differenceMinor)}` : 'Falla')}</td></tr>`,
    )
    .join('');
  const others = [
    ...t.adjustments.map(
      (a) =>
        `<tr><td>${Number(a.percent) < 0 ? 'Descuento' : 'Recargo'}</td><td>${esc(a.label)}</td><td class="small">${source(a.provenance)}</td></tr>`,
    ),
    ...t.underwriting.map(
      (u) =>
        `<tr><td>${pill(u.decision)}</td><td>${esc(u.label)} <span class="mono">(${esc(u.code)})</span><div class="muted small">${esc(u.reason)}</div></td><td class="small">${source(u.provenance)}</td></tr>`,
    ),
    `<tr><td>Gastos de emisión</td><td>${q(t.fees.issuanceFee.amountMinor)}</td><td class="small">${source(t.fees.issuanceFee.provenance)}</td></tr>`,
    `<tr><td>IVA</td><td>${esc(t.fees.vat.percent)} %</td><td class="small">${source(t.fees.vat.provenance)}</td></tr>`,
    `<tr><td>Vigencia</td><td>${t.fees.validityDays.days} días</td><td class="small">${source(t.fees.validityDays.provenance)}</td></tr>`,
    ...t.fees.frequencies.map(
      (f) =>
        `<tr><td>Pago ${esc((FREQ_NAME[f.frequency] ?? f.frequency).toLowerCase())}</td><td>${f.installments} cuota${f.installments === 1 ? '' : 's'} · recargo ${esc(f.surchargePercent)} %</td><td class="small">${source(f.provenance)}</td></tr>`,
    ),
  ].join('');
  const decide =
    rs.state !== 'PROPOSED'
      ? ''
      : director
        ? `<div class="rule-decision"><div class="field">${textareaControl({ label: 'Motivo si la rechazas', id: 'rule-reason', rows: 2, maxlength: 500, placeholder: 'Por ejemplo: la tarifa cambia el 1 de noviembre' })}</div>
          <div class="actions-row"><button type="button" class="btn danger" data-rule-reject="${esc(rs.id)}">Rechazar propuesta</button>
          <button type="button" class="btn primary" data-rule-publish="${esc(rs.id)}" ${failing ? 'aria-disabled="true"' : ''}>Publicar versión ${rs.version}</button></div>
          ${failing ? `<p class="note bad">${icon('alert')}<span>No se puede publicar: ${failing} de ${rs.tests.total} casos de prueba no pasan.</span></p>` : ''}</div>`
        : `<p class="note info">${icon('alert')}<span>La dirección de la agencia revisa la propuesta y decide si se publica.</span></p>`;
  return `<section class="card" id="rule-detail" tabindex="-1" aria-labelledby="rule-detail-title">
    <div class="card-head"><h2 id="rule-detail-title">${esc(rs.carrierName)} · ${esc(lineName(rs.productLine))} · versión ${rs.version}</h2><span class="spacer"></span>${ruleState(rs.state)}
      <button type="button" class="btn sm" data-rule-close aria-label="Cerrar las reglas">Cerrar</button></div>
    <div class="rule-meta small">
      <div><span class="muted">Origen</span> ${rs.source ? `${esc(rs.source.fileName)} · <span class="mono" title="sha256">${esc(rs.source.sha256.slice(0, 12))}…</span>${rs.source.mappedBy === 'llm' ? ' · formato reconocido por el asistente' : ''}` : 'escrita a mano'}</div>
      <div><span class="muted">Plan</span> ${esc(rs.plan.name)} <span class="mono">(${esc(rs.plan.code)})</span></div>
      <div><span class="muted">Casos de prueba</span> ${rs.tests.passed} de ${rs.tests.total} · exactitud ${esc(rs.tests.accuracy)} %</div>
      ${rs.rejection ? `<div><span class="muted">Motivo del rechazo</span> ${esc(rs.rejection)}</div>` : ''}
    </div>
    ${decide}
    <h3 class="sub">Casos de prueba de la hoja</h3>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Ejemplo</th><th>La aseguradora dice</th><th>Las reglas dan</th><th>Resultado</th></tr></thead><tbody>${tests}</tbody></table></div>
    <h3 class="sub">Tarifa (${t.rating.rows.length} valores)</h3>
    <div class="table-wrap rule-table" data-component="rule-table"><table class="table"><thead><tr>${ratingHead}<th>Procedencia</th></tr></thead><tbody>${ratingRows}</tbody></table></div>
    <h3 class="sub">Recargos, suscripción, gastos e impuestos</h3>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Regla</th><th>Valor</th><th>Procedencia</th></tr></thead><tbody>${others}</tbody></table></div>
  </section>`;
}

/** «Reglas de aseguradoras»: RuleSets per carrier × line, uploads and proposals (management). */
function rulesSection(list: RuleSetSummary[], matrix: Matrix) {
  const byRules = [
    ...new Map(
      matrix.rows
        .filter((r) => r.cells.quote?.mode === 'RULES')
        .map((r) => [r.carrier, r.carrierName]),
    ).entries(),
  ];
  const upload = byRules.length
    ? `<form id="rule-upload-form" class="rule-upload" novalidate>
        ${field(
          'Aseguradora',
          selectControl(
            { id: 'rule-carrier' },
            byRules.map(([id, name]) => ({ value: id, label: name })),
          ),
        )}
        ${fileField('Hoja de tarifas (.xlsx)', { id: 'rule-file', accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', hint: 'Excel .xlsx · hasta 2 MB' })}
        <button type="submit" class="btn primary" id="rule-upload">${icon('inbox')}Subir hoja de tarifas</button>
      </form>`
    : '';
  const rows = list.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Aseguradora</th><th>Producto</th><th class="num">Versión</th><th>Estado</th><th>Origen</th><th>Casos de prueba</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${list
        .map(
          (
            r,
          ) => `<tr${ruleDetail?.id === r.id ? ' class="selected"' : ''}><td>${carrierCell(r.carrier, r.carrierName)}</td><td>${esc(lineName(r.productLine))}</td>
          <td class="num">${r.version}</td><td>${ruleState(r.state)}${r.published && r.state === 'PUBLISHED' ? `<div class="muted small">desde el ${esc(date(r.published.at))}</div>` : ''}</td>
          <td class="small">${esc(r.source?.fileName ?? 'a mano')}<div class="muted">${esc(date(r.createdAt))}</div></td>
          <td>${r.tests.passed === r.tests.total && r.tests.total ? pill('ok', `${r.tests.passed} de ${r.tests.total}`) : pill('bad', `${r.tests.passed} de ${r.tests.total}`)} <span class="muted small">${esc(r.tests.accuracy)} %</span></td>
          <td class="actions"><button type="button" class="btn sm" data-rule-open="${esc(r.id)}">${r.state === 'PROPOSED' ? 'Revisar propuesta' : 'Ver reglas'}</button></td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : `<p class="muted small rule-empty">Aún no hay reglas. Sube la hoja de tarifas de una aseguradora sin API para cotizarla con precios indicativos.</p>`;
  return `<section class="card" id="reglas"><div class="card-head"><h2>Reglas de aseguradoras</h2><span class="spacer"></span>
      <span class="muted small">Precios indicativos para aseguradoras sin API</span></div>
    <p class="note info">${icon('alert')}<span>Nubi lee la hoja de tarifas y propone las reglas con la celda de donde salió cada valor. Solo la dirección publica, y solo si la propuesta reproduce todos los ejemplos de la hoja. Las cotizaciones con estas reglas son <strong>indicativas</strong>: la aseguradora confirma al emitir.</span></p>
    ${upload}${rows}</section>${ruleDetail ? ruleDetailView(ruleDetail) : ''}`;
}

/** Forgets the proposal left open (a new session starts with the list). */
export function resetRules() {
  ruleDetail = null;
  resetPlans();
}

export const actions: Actions = {
  click: {
    ...planActions.click,
    ruleOpen: (t, d) => {
      void act(t, 'Abriendo las reglas…', async () => {
        ruleDetail = await api<RuleSetFull>(
          'GET',
          `/v1/rule-sets/${encodeURIComponent(d.ruleOpen ?? '')}`,
        );
        await render(current, { quiet: true });
        $('#rule-detail')?.focus();
        $('#rule-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return `Versión ${ruleDetail.version} de ${lineName(ruleDetail.productLine).toLowerCase()} de ${ruleDetail.carrierName}.`;
      });
    },
    ruleClose: () => {
      ruleDetail = null;
      void render(current, { quiet: true });
    },
    rulePublish: (t, d) => {
      if (t.getAttribute('aria-disabled') === 'true') {
        toast('No se puede publicar: hay casos de prueba que no pasan.', 'error');
        return;
      }
      void act(t, 'Publicando las reglas…', async () => {
        const r = await api<RuleSetSummary & { retired: string | null }>(
          'POST',
          `/v1/rule-sets/${encodeURIComponent(d.rulePublish ?? '')}/publish`,
          {},
        );
        ruleDetail = await api<RuleSetFull>('GET', `/v1/rule-sets/${encodeURIComponent(r.id)}`);
        await render(current, { quiet: true });
        return `Versión ${r.version} publicada: las cotizaciones de ${lineName(r.productLine).toLowerCase()} con ${r.carrierName} ya usan estas reglas${r.retired ? ' y la versión anterior quedó retirada' : ''}.`;
      });
    },
    ruleReject: (t, d) => {
      void act(t, 'Rechazando la propuesta…', async () => {
        const reason = textInput('#rule-reason', 'el motivo del rechazo');
        const r = await api<RuleSetSummary>(
          'POST',
          `/v1/rule-sets/${encodeURIComponent(d.ruleReject ?? '')}/reject`,
          { reason },
        );
        ruleDetail = await api<RuleSetFull>('GET', `/v1/rule-sets/${encodeURIComponent(r.id)}`);
        await render(current, { quiet: true });
        return `Propuesta versión ${r.version} rechazada.`;
      });
    },
  },
  submit: {
    ...planActions.submit,
    'rule-upload-form': (ev) => {
      ev.preventDefault();
      void act($('#rule-upload'), 'Leyendo la hoja de tarifas…', async () => {
        const file = ($('#rule-file') as HTMLInputElement).files?.[0];
        if (!file) throw new InputError('Elige la hoja de tarifas (.xlsx).');
        if (!/\.xlsx$/i.test(file.name)) throw new InputError('La hoja debe ser un archivo .xlsx.');
        if (file.size > 2 * 1024 * 1024) throw new InputError('La hoja pesa más de 2 MB.');
        const r = await api<{ proposals: RuleSetFull[] }>('POST', '/v1/rule-sets/imports', {
          carrier: ($('#rule-carrier') as HTMLSelectElement).value,
          fileName: file.name,
          contentBase64: await fileBase64(file),
        });
        ruleDetail = r.proposals[0] ?? null;
        await render(current, { quiet: true });
        $('#rule-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return `${r.proposals.length} propuesta${r.proposals.length === 1 ? '' : 's'} de reglas: ${r.proposals
          .map(
            (p) =>
              `${lineName(p.productLine).toLowerCase()} v${p.version} (${p.tests.passed} de ${p.tests.total} casos)`,
          )
          .join(', ')}.`;
      });
    },
  },
  changeData: { ...planActions.changeData },
};
